import { useEffect, useRef, useState } from 'react';
import { getSocket } from '../api/socket';

const MAX_LINES = 500;

interface UseLogStreamResult {
  lines: string[];
  connected: boolean;
}

/**
 * Subscribes to the live log stream for `unit` via socket.io.
 * - Replays the server-side ring buffer (last 500 lines) immediately on subscribe.
 * - Re-subscribes automatically on socket reconnect.
 * - Pass `null` to suspend streaming (e.g. when a drawer is closed).
 */
export function useLogStream(unit: string | null): UseLogStreamResult {
  const [lines, setLines]       = useState<string[]>([]);
  const [connected, setConnected] = useState(false);
  const unitRef = useRef(unit);
  unitRef.current = unit;

  useEffect(() => {
    if (!unit) {
      setLines([]);
      setConnected(false);
      return;
    }

    const socket = getSocket();

    const doSubscribe = () => {
      socket.emit('log:subscribe', { unit });
      setConnected(socket.connected);
    };

    const handleLine = (payload: { unit: string; line: string }) => {
      if (payload.unit !== unitRef.current) return;
      setLines((prev) => {
        const next = [...prev, payload.line];
        return next.length > MAX_LINES ? next.slice(-MAX_LINES) : next;
      });
    };

    const handleConnect = () => {
      setLines([]);         // ring buffer replay comes fresh from server
      doSubscribe();
    };

    const handleDisconnect = () => setConnected(false);

    socket.on('log:line', handleLine);
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    doSubscribe();

    return () => {
      socket.emit('log:unsubscribe', { unit });
      socket.off('log:line', handleLine);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      setLines([]);
      setConnected(false);
    };
  }, [unit]);

  return { lines, connected };
}
